import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { scrollTo as themeScrollTo } from '@theme/scroll-container';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // Handle variant select change (delegated)
    this.addEventListener('change', (event) => {
      if (event.target && event.target.matches('.sticky-add-to-cart__variant-select')) {
        this.handleVariantSelectChange(event);
      }
    });


    // Listen for size system changes (EU/US/UK)
    const handleSizeSystemChange = (e) => {
      const system = e.detail?.system || localStorage.getItem('shopify_size_system');
      if (system) {
        this.applySizeSystem(system);
      }
    };
    window.addEventListener('size-system-changed', handleSizeSystemChange, { signal });
    window.addEventListener(
      'storage',
      (e) => {
        if (e.key === 'shopify_size_system') {
          this.applySizeSystem(e.newValue);
        }
      },
      { signal }
    );

    // Apply preferred size system on initial load
    this.applySizeSystem();

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Applies the chosen size system (EU, US, UK) to all options in the variant select.
   * @param {string} [system] - 'EU', 'US', or 'UK'
   */
  applySizeSystem(system) {
    const preferred = (system || localStorage.getItem('shopify_size_system') || 'EU').toLowerCase();
    const select = this.querySelector('.sticky-add-to-cart__variant-select');
    if (!select) return;

    for (const option of select.options) {
      const localized = option.getAttribute(`data-title-${preferred}`);
      if (localized) {
        option.textContent = localized;
      }
    }
  }

  /**
   * Handles the variant selection from the dropdown
   * @param {Event} event - The change event
   */
  handleVariantSelectChange = (event) => {
    const select =
      event.target instanceof HTMLSelectElement
        ? event.target
        : this.querySelector('.sticky-add-to-cart__variant-select');
    if (!select || !select.selectedOptions || !select.selectedOptions[0]) return;

    const option = select.selectedOptions[0];
    const variantId = option.value;
    const available = option.dataset.available === 'true';
    const price = option.dataset.price;
    const compareAtPrice = option.dataset.compareAtPrice;
    const image = option.dataset.image;
    const opt1 = option.dataset.option1;
    const opt2 = option.dataset.option2;
    const opt3 = option.dataset.option3;

    // 1. Update dataset on component
    this.dataset.currentVariantId = variantId;
    this.dataset.variantAvailable = available ? 'true' : 'false';

    // 2. Update button state and text
    const addToCartButton = this.refs.addToCartButton || this.querySelector('.sticky-add-to-cart__button');
    if (addToCartButton) {
      addToCartButton.disabled = !available;
      const addText = available
        ? this.dataset.addToCartText || 'Ajouter au panier'
        : this.dataset.soldOutText || 'Épuisé';

      addToCartButton.setAttribute('aria-label', addText);

      const contentSpan = addToCartButton.querySelector('.add-to-cart-text__content span:first-child');
      if (contentSpan) {
        contentSpan.textContent = addText;
      }

      const visuallyHiddenSpan = addToCartButton.querySelector('.visually-hidden');
      if (visuallyHiddenSpan) {
        visuallyHiddenSpan.textContent = addText;
      }
    }

    // 3. Update price display if present
    const priceDisplay = this.querySelector('.sticky-add-to-cart__price');
    if (priceDisplay && price) {
      if (compareAtPrice) {
        priceDisplay.innerHTML = `<span class="price-container price-container--on-sale"><span class="price-item__group price price--sale price-current--sale">${price}</span> <s class="price-item__group compare-at-price price-item--compare-at">${compareAtPrice}</s></span>`;
      } else {
        priceDisplay.innerHTML = `<span class="price">${price}</span>`;
      }
    }

    // 4. Update thumbnail image if present
    if (image && this.refs.productImage) {
      this.refs.productImage.src = image;
    }

    // 5. Update main product form's variant ID input immediately
    const productForm = this.#getProductForm();
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
      }
    }

    // 6. Synchronize with main variant picker
    const productId = this.dataset.productId;
    const variantPicker =
      document.querySelector(`variant-picker[data-product-id="${productId}"]`) ||
      document.querySelector('variant-picker');

    if (variantPicker) {
      this.#syncVariantPicker(variantPicker, [opt1, opt2, opt3], variantId);
    } else {
      const url = new URL(window.location.href);
      if (variantId) {
        url.searchParams.set('variant', variantId);
      }
      if (url.href !== window.location.href) {
        history.replaceState({}, '', url.toString());
      }
    }
  };

  /**
   * Synchronizes the main variant-picker with the selected option values
   * @param {HTMLElement} variantPicker - The variant-picker element
   * @param {string[]} optionValues - Array of option values [option1, option2, option3]
   * @param {string} variantId - The selected variant ID
   */
  #syncVariantPicker(variantPicker, optionValues, variantId) {
    if (!variantPicker) return;

    const fieldsets = Array.from(variantPicker.querySelectorAll('fieldset.variant-option'));
    const selects = Array.from(variantPicker.querySelectorAll('select.variant-option__select'));

    let lastTargetElement = null;
    let elementWithMedia = null;

    optionValues.forEach((val, index) => {
      if (val === undefined || val === null || val === '') return;
      const cleanVal = val.trim();

      // Check fieldsets (buttons, swatches, images)
      if (fieldsets[index]) {
        const fieldset = fieldsets[index];
        const inputs = Array.from(fieldset.querySelectorAll('input'));
        const matchedInput = inputs.find(
          (input) => input.value.trim() === cleanVal || input.value.trim().toLowerCase() === cleanVal.toLowerCase()
        );

        if (matchedInput) {
          matchedInput.checked = true;
          if (typeof variantPicker.updateSelectedOption === 'function') {
            variantPicker.updateSelectedOption(matchedInput);
          }
          lastTargetElement = matchedInput;
          if (matchedInput.dataset.mediaId || matchedInput.closest('[data-media-id]')) {
            elementWithMedia = matchedInput;
          }
        }
      }

      // Check dropdown selects
      if (selects[index]) {
        const select = selects[index];
        const options = Array.from(select.options);
        const matchedOption = options.find(
          (opt) => opt.value.trim() === cleanVal || opt.value.trim().toLowerCase() === cleanVal.toLowerCase()
        );

        if (matchedOption) {
          select.value = matchedOption.value;
          for (const opt of select.options) {
            opt.removeAttribute('selected');
          }
          matchedOption.setAttribute('selected', 'selected');
          if (typeof variantPicker.updateSelectedOption === 'function') {
            variantPicker.updateSelectedOption(select);
          }
          lastTargetElement = select;
          if (matchedOption.dataset.mediaId || select.closest('[data-media-id]')) {
            elementWithMedia = select;
          }
        }
      }
    });

    const triggerElement = elementWithMedia || lastTargetElement;
    if (triggerElement) {
      triggerElement.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { fromSticky: true } }));
    }
  }

  /**
   * Smoothly scrolls to the top of the page
   */
  handleScrollToTop = (event) => {
    event?.preventDefault?.();
    themeScrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton || !this.#targetAddToCartButton.isConnected) {
      const productForm = this.#getProductForm();
      this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') || null;
    }
    if (!this.#targetAddToCartButton) {
      const fallbackBtn = document.querySelector('product-form-component button[type="submit"]');
      if (fallbackBtn instanceof HTMLButtonElement) {
        this.#targetAddToCartButton = fallbackBtn;
      }
    }
    if (!this.#targetAddToCartButton) return;

    if (this.refs.addToCartButton && this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    onAnimationEnd([this.refs.addToCartButton, flyToCartElement]).then(() => {
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton?.removeAttribute('data-added');
      }, 800);
    });
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
          // Synchronize dropdown value with the selected variant
          const select = this.querySelector('.sticky-add-to-cart__variant-select');
          if (select) {
            select.value = variant.id.toString();
          }
        }

        // Apply size system immediately after morphing
        this.applySizeSystem();

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  #formatSizeOption = (val) => {
    if (!val) return '';
    const valLower = val.toLowerCase();
    if (valLower.includes('eu') && valLower.includes('us')) {
      const tokens = val.split('/');
      let sizeEu = '', sizeUs = '', sizeUk = '', nonSize = '';
      for (const t of tokens) {
        const trim = t.trim();
        const up = trim.toUpperCase();
        if (up.includes('EU') || up.includes('FR')) sizeEu = trim;
        else if (up.includes('US')) sizeUs = trim;
        else if (up.includes('UK')) sizeUk = trim;
        else nonSize = nonSize ? `${nonSize} / ${trim}` : trim;
      }
      const chosen = sizeEu || sizeUs || sizeUk;
      return nonSize ? `${nonSize} / ${chosen}` : chosen;
    }
    return val;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => this.#formatSizeOption(/** @type {HTMLInputElement} */ (option).value))
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
